/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import {
	type FormEventHandler,
	forwardRef,
	type ForwardRefExoticComponent,
	type Ref,
	type RefAttributes,
	type RefObject,
	useEffect,
	useState,
} from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import CrossIcon from '@atlaskit/icon/core/cross';
import EditorSearchIcon from '@atlaskit/icon/core/search';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box } from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import Spinner from '@atlaskit/spinner/spinner';
import Textfield from '@atlaskit/textfield/text-field';
import { token } from '@atlaskit/tokens';
import {
	addRecentSearch,
	RECENT_SEARCH_KEY,
} from '@atlassian/dynamic-cards-search-common/utils/recent-searches';

import { UserStatus } from '../../../../common/constants/types';
import { useAppContext } from '../../../../common/ui/app-context';
import {
	useBootstrap,
	useDropdown,
	useFeatures,
	useInputQuery,
	useIsLoading,
	useSearchQuery,
	useUser,
} from '../../../../controllers/store';
import { type StyleOverrides } from '../../../../controllers/store/bootstrap';
import { messages } from './messages';

const textFieldBoxStyles = xcss({
	height: 'space.500',
	flexGrow: 1,
});

const cssOverrideWrapperStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
	'> div': {
		height: '40px',
		borderWidth: token('border.width'),
		borderRadius: token('radius.medium'),
	},
});

const searchIconWrapperStyles = xcss({
	minWidth: 'size.200',
	minHeight: 'size.200',
	marginLeft: 'space.100',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});

const clearIconStyles = xcss({
	marginRight: 'space.100',
});

export type SearchTextFieldProps = {
	hasReceivedFocus?: () => void;
	isReadOnly?: boolean;
	keyboardRef?: RefObject<HTMLInputElement>;
	onSubmit?: () => void;
	styleOverrides?: StyleOverrides['textfield'];
};

export const TEXTFIELD_ID = 'ch-search-text-query-input';

export const SearchTextField: ForwardRefExoticComponent<
	SearchTextFieldProps & RefAttributes<HTMLDivElement>
> = forwardRef((props: SearchTextFieldProps, ref: Ref<HTMLDivElement>) => {
	const { onSubmit } = props;
	let hasReceivedFocus = props.hasReceivedFocus ? props.hasReceivedFocus : () => {};

	const [isLoading] = useIsLoading();
	const intl = useIntl();
	const [query, { setInputQuery, triggerSearch }] = useSearchQuery();
	const [localQuery, setLocalQuery] = useState(query);
	const [{ primaryProduct, user }] = useBootstrap();
	const [userWithStatus] = useUser();
	const [showDropdown, { setShowDropdown }] = useDropdown();
	const [features] = useFeatures();
	const isUserAuthenticated = userWithStatus.status === UserStatus.Authenticated;

	// Sync local query with input query
	useEffect(() => {
		setInputQuery(localQuery);
	}, [setInputQuery, localQuery]);

	// Sync search query with local query
	// on search query change, determine intent / get smart answer route
	useEffect(() => {
		setLocalQuery(query);
	}, [query, setLocalQuery]);

	const [inputQuery] = useInputQuery();
	const { generateSearchUrl, onNavigate, queryParams } = useAppContext();

	const handleOnSubmit = () => {
		// Store search in recent searches
		if (localQuery.trim() !== '' && user && user.id && primaryProduct) {
			addRecentSearch(localQuery, user.id, primaryProduct, RECENT_SEARCH_KEY);
		}

		// Handles the delegated UI events like closing the dropdown
		if (onSubmit) {
			onSubmit();
		}

		if (features?.chromeExtension?.enabled) {
			features.chromeExtension.onInputSubmit({ query: localQuery });
			return;
		}

		// If quick search is enabled, we don't want to trigger whole page search
		if (features?.quickSearch?.enabled) {
			onNavigate(
				generateSearchUrl({
					...queryParams,
					text: inputQuery,
				}),
				'push',
			);

			return;
		}

		triggerSearch();
	};

	const onChange: FormEventHandler<HTMLInputElement> = (e) => {
		setLocalQuery(e.currentTarget.value);
		hasReceivedFocus();
		if (e.currentTarget.value === '' && !isUserAuthenticated) {
			setShowDropdown(false);
		} else if (e.currentTarget.value !== '') {
			setShowDropdown(true);
		}
	};

	const onClick = () => {
		hasReceivedFocus();
		if (localQuery === '' && !isUserAuthenticated) {
			setShowDropdown(false);
		}
	};

	const onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter') {
			handleOnSubmit();
			event.currentTarget.blur();
		}
	};

	const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'ArrowDown') {
			hasReceivedFocus();
		}
		if (showDropdown && event.key === 'Tab') {
			if (onSubmit) {
				onSubmit();
			}
		}
	};

	return (
		<Box xcss={textFieldBoxStyles} ref={ref}>
			<div
				css={[
					cssOverrideWrapperStyles,
					// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
					props.styleOverrides?.borderRadius && {
						'&& [data-ds--text-field--container]': {
							borderRadius: props.styleOverrides?.borderRadius,
						},
					},
					// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
					props.styleOverrides?.textColor && {
						'&& [data-ds--text-field--input]': { color: props.styleOverrides?.textColor },
						'&& [data-ds--text-field--input]::placeholder': {
							color: props.styleOverrides?.textColor,
						},
					},
					// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
					props.styleOverrides?.backgroundColor && {
						'&& [data-ds--text-field--container]': {
							backgroundColor: props.styleOverrides?.backgroundColor,
						},
						'&& [data-ds--text-field--container]:hover': {
							backgroundColor: props.styleOverrides?.backgroundColor,
						},
						'&& [data-ds--text-field--container]:focus-within': {
							backgroundColor: props.styleOverrides?.backgroundColor,
						},
					},
				]}
			>
				<Textfield
					ref={props.keyboardRef}
					id={TEXTFIELD_ID}
					testId={TEXTFIELD_ID}
					value={localQuery}
					onKeyPress={(event: React.KeyboardEvent<HTMLInputElement>) => onKeyPress(event)}
					onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => onKeyDown(event)}
					onChange={onChange}
					onClick={onClick}
					autoComplete="off"
					isReadOnly={props.isReadOnly}
					placeholder={
						props.styleOverrides?.placeholder ??
						intl.formatMessage(messages.inputPlaceholderSearchOnly)
					}
					elemBeforeInput={
						<Box xcss={searchIconWrapperStyles}>
							{isLoading ? (
								<Spinner testId="search-page-input-spinner" size="small" appearance="inherit" />
							) : (
								<EditorSearchIcon
									color={
										props.styleOverrides?.textColor
											? (props.styleOverrides?.textColor as React.ComponentProps<
													typeof EditorSearchIcon
												>['color'])
											: 'currentColor'
									}
									spacing="spacious"
									label={intl.formatMessage(messages.searchIconLabel)}
								/>
							)}
						</Box>
					}
					elemAfterInput={
						localQuery && (
							<Box xcss={clearIconStyles}>
								<IconButton
									icon={(iconProps) => (
										<CrossIcon
											{...iconProps}
											color={
												props.styleOverrides?.textColor
													? (props.styleOverrides?.textColor as React.ComponentProps<
															typeof CrossIcon
														>['color'])
													: 'currentColor'
											}
											label={intl.formatMessage(messages.clearIconLabel)}
										/>
									)}
									spacing={'compact'}
									onClick={() => setLocalQuery('')}
									label={intl.formatMessage(messages.clearIconLabel)}
									appearance={'subtle'}
								/>
							</Box>
						)
					}
				/>
			</div>
		</Box>
	);
});
